import axios from 'axios';
import * as globals from '../../components/GlobalScripts.js';

const ECUADOR_TIME_ZONE = 'America/Guayaquil';
export const ORDER_CUTOFF_HOUR_EC = 18;

export function getPathApi() {
    return String(localStorage.getItem('path_api') || '').replace(/\/$/, '');
}

export function buildAuthPayload() {
    return {
        rute: globals.getRuteBackend(),
        session_id: globals.get_session_id(),
        keylogin: globals.get_keylogin(),
    };
}

function getEcNowParts() {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: ECUADOR_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date());

    const map = {};
    parts.forEach(({ type, value }) => {
        if (type !== 'literal') map[type] = value;
    });

    return {
        date: `${map.year}-${map.month}-${map.day}`,
        hour: Number(map.hour || 0),
        minute: Number(map.minute || 0),
        second: Number(map.second || 0),
    };
}

function addDaysEc(iso, days) {
    const d = new Date(`${iso}T12:00:00-05:00`);
    d.setDate(d.getDate() + days);
    return d
        .toLocaleDateString('en-CA', { timeZone: ECUADOR_TIME_ZONE })
        .split('T')[0];
}

export function getTodayEc() {
    return getEcNowParts().date;
}

export function getMinOrderDayEc(cutoffHour = ORDER_CUTOFF_HOUR_EC) {
    const nowEc = getEcNowParts();
    const minDaysAhead = nowEc.hour >= cutoffHour ? 2 : 1;
    return addDaysEc(nowEc.date, minDaysAhead);
}

export function dateOnly(dt) {
    return String(dt || '').slice(0, 10);
}

export const DAY_LABELS = {
    mon: 'Lunes',
    tue: 'Martes',
    wed: 'Miércoles',
    thu: 'Jueves',
    fri: 'Viernes',
};

export const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri'];

export function formatEcDate(iso) {
    if (!iso) return '';
    const d = new Date(`${iso}T12:00:00-05:00`);
    return d.toLocaleDateString('es-EC', {
        timeZone: ECUADOR_TIME_ZONE,
        day: '2-digit',
        month: '2-digit',
    });
}

export function computeDayDateMapFromWeekRange(startDate, endDate) {
    const map = {};
    if (!startDate || !endDate) return map;

    const start = new Date(`${startDate}T12:00:00-05:00`);
    const end = new Date(`${endDate}T12:00:00-05:00`);

    for (let i = 0; i < 14; i++) {
        const cur = new Date(start.getTime());
        cur.setDate(start.getDate() + i);

        if (cur.getTime() > end.getTime()) break;

        const dayKey = cur
            .toLocaleDateString('en-US', { timeZone: ECUADOR_TIME_ZONE, weekday: 'short' })
            .slice(0, 3)
            .toLowerCase();

        const iso = cur
            .toLocaleDateString('en-CA', { timeZone: ECUADOR_TIME_ZONE })
            .split('T')[0];

        if (!map[dayKey]) map[dayKey] = iso;
    }

    return map;
}

function safeJsonParse(maybeJson, fallback = null) {
    try {
        if (typeof maybeJson === 'string') {
            const s = maybeJson.trim();
            if (!s) return fallback;
            return JSON.parse(s);
        }
        if (maybeJson && typeof maybeJson === 'object') return maybeJson;
    } catch { }
    return fallback;
}

function toNum(v, fallback = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

function toNumOrNull(v) {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function normalizeImages(raw) {
    const arr = safeJsonParse(raw, raw);
    if (!Array.isArray(arr)) return [];
    return arr
        .map((x) => String(x || '').trim())
        .filter(Boolean);
}

function normalizeResumen(resumen, fallbackResumen = null) {
    const r = safeJsonParse(resumen, resumen);
    const fb = safeJsonParse(fallbackResumen, fallbackResumen);

    const src = r && typeof r === 'object' ? r : (fb && typeof fb === 'object' ? fb : null);
    if (!src) return null;

    return {
        kcal: toNum(src?.kcal ?? src?.calorias ?? src?.calorias_kcal, 0),
        proteinas: toNum(src?.proteinas ?? src?.proteina ?? src?.proteinas_g, 0),
        carbohidratos: toNum(src?.carbohidratos ?? src?.carbs ?? src?.carbohidratos_totales_g, 0),
        grasas: toNum(src?.grasas ?? src?.grasas_totales_g, 0),
    };
}

function normalizeNutricion(dataObj, macrosNorm) {
    const rawNut = safeJsonParse(dataObj?.nutricion, dataObj?.nutricion || {});
    const nut = rawNut && typeof rawNut === 'object' ? rawNut : {};

    const resumen = normalizeResumen(nut?.resumen, dataObj?.macros) || macrosNorm || { kcal: 0, proteinas: 0, carbohidratos: 0, grasas: 0 };

    const rawPorcion = safeJsonParse(nut?.porcion, nut?.porcion || {});
    const porcionObj = rawPorcion && typeof rawPorcion === 'object' ? rawPorcion : {};
    const porcion = {
        tamano: toNumOrNull(porcionObj?.tamano),
        unidad: String(porcionObj?.unidad || '').trim(),
        porciones_por_envase: toNumOrNull(porcionObj?.porciones_por_envase),
    };

    const rawTabla = safeJsonParse(nut?.tabla, nut?.tabla || {});
    const tablaObj = rawTabla && typeof rawTabla === 'object' ? rawTabla : {};

    const tabla = {
        calorias_kcal: toNumOrNull(tablaObj?.calorias_kcal ?? tablaObj?.calorias ?? resumen?.kcal),
        grasas_totales_g: toNumOrNull(tablaObj?.grasas_totales_g ?? tablaObj?.grasas ?? resumen?.grasas),
        grasas_saturadas_g: toNumOrNull(tablaObj?.grasas_saturadas_g),
        grasas_trans_g: toNumOrNull(tablaObj?.grasas_trans_g),
        colesterol_mg: toNumOrNull(tablaObj?.colesterol_mg),
        sodio_mg: toNumOrNull(tablaObj?.sodio_mg),
        carbohidratos_totales_g: toNumOrNull(tablaObj?.carbohidratos_totales_g ?? tablaObj?.carbohidratos ?? resumen?.carbohidratos),
        fibra_dietetica_g: toNumOrNull(tablaObj?.fibra_dietetica_g),
        azucares_totales_g: toNumOrNull(tablaObj?.azucares_totales_g),
        azucares_anadidos_g: toNumOrNull(tablaObj?.azucares_anadidos_g),
        proteinas_g: toNumOrNull(tablaObj?.proteinas_g ?? tablaObj?.proteinas ?? resumen?.proteinas),
    };

    const rawExtra = safeJsonParse(nut?.extra, nut?.extra || []);
    const extraArr = Array.isArray(rawExtra) ? rawExtra : [];

    const extra = extraArr
        .map((x) => ({
            nombre: String(x?.nombre || '').trim(),
            cantidad: toNumOrNull(x?.cantidad),
            unidad: String(x?.unidad || '').trim(),
            vd: String(x?.vd || '').trim(),
        }))
        .filter((x) => x.nombre);

    const notas = String(nut?.notas || dataObj?.notas_nutricion || '').trim();

    return { resumen, porcion, tabla, extra, notas };
}

export async function fetchPublishedMenu() {
    const api = getPathApi();
    const payload = { ...buildAuthPayload() };
    const res = await axios.post(`${api}/admin/menu_week/get.php`, payload);
    if (res.data?.mensaje !== 'success') return null;
    return res.data?.published ?? null;
}

export async function fetchPlatillosTipo(tipo) {
    const api = getPathApi();

    const payload = {
        ...buildAuthPayload(),
        q: '',
        tipo,
        activo: 1,
        page: 1,
        limit: 1000,
    };

    const res = await axios.post(`${api}/admin/platillos/list.php`, payload);
    if (res.data?.mensaje !== 'success') return [];

    const items = Array.isArray(res.data?.items) ? res.data.items : [];

    return items
        .map((r) => {
            const data = safeJsonParse(r?.data, r?.data || {}) || {};

            const imagenes = normalizeImages(data?.imagenes);

            const macrosNorm =
                normalizeResumen(r?.macros, data?.macros) ||
                normalizeResumen(data?.nutricion?.resumen, data?.nutricion) ||
                { kcal: 0, proteinas: 0, carbohidratos: 0, grasas: 0 };

            const nutricion = normalizeNutricion(data, macrosNorm);

            return {
                id: Number(r?.id || 0) || null,
                tipo: String(r?.tipo || data?.tipo || tipo || ''),
                activo: Number(r?.activo ?? 1) ? 1 : 0,

                nombre: String(data?.nombre || ''),
                descripcion: String(data?.descripcion || ''),
                alergenos: String(data?.alergenos_disclaimer || ''),
                disclaimer: String(data?.disclaimer || ''),

                imagenes,

                macros: macrosNorm,

                nutricion,
            };
        })
        .filter((x) => !!x.id);
}

export async function fetchPlatillosMap() {
    const [entradas, fuertes, snacks] = await Promise.all([
        fetchPlatillosTipo('entrada'),
        fetchPlatillosTipo('fuerte'),
        fetchPlatillosTipo('snack'),
    ]);

    const map = {};
    [...entradas, ...fuertes, ...snacks].forEach((p) => {
        map[String(p.id)] = p;
    });

    return map;
}

export async function fetchOrdersWeek({ start_date, end_date }) {
    const api = getPathApi();
    const payload = { ...buildAuthPayload(), start_date, end_date };
    const res = await axios.post(`${api}/cliente/orders/week_list.php`, payload);

    if (res.data?.mensaje !== 'success') {
        return { orders: [], account: null, server_today: null };
    }

    return {
        orders: Array.isArray(res.data?.orders) ? res.data.orders : [],
        account: res.data?.account ?? null,
        server_today: res.data?.server_today ?? null,
    };
}

export async function createOrder({ order_day, type, dish_info }) {
    const api = getPathApi();
    const payload = { ...buildAuthPayload(), order_day, type, dish_info };
    const res = await axios.post(`${api}/cliente/orders/create.php`, payload);
    return res.data;
}

export async function updateOrder({ id, dish_info }) {
    const api = getPathApi();
    const payload = { ...buildAuthPayload(), id, dish_info };
    const res = await axios.post(`${api}/cliente/orders/update.php`, payload);
    return res.data;
}

export async function cancelOrder({ id }) {
    const api = getPathApi();
    const payload = { ...buildAuthPayload(), id };
    const res = await axios.post(`${api}/cliente/orders/cancel.php`, payload);
    return res.data;
}
